import React,{useState,useEffect,useCallback,useRef} from 'react';
import {branchAPI,userAPI,uploadAPI,notificationAPI} from '../utils/api';
import {PageHeader,SearchBar,Badge,Modal,FormInput} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import {
  Plus,Edit,Trash2,ToggleLeft,ToggleRight,Users,ChevronDown,ChevronUp,
  MapPin,Phone,Mail,Clock,Shield,Send,Check,X,Edit2,RefreshCw,
  MessageSquare,CheckCircle,XCircle,Image,Upload,AlertTriangle,Building2,Link,
  QrCode,Download,ExternalLink,Smartphone,Copy,Lock
} from 'lucide-react';
import {QRCodeSVG} from 'qrcode.react';
import toast from 'react-hot-toast';

const ROLE_COLOR={Owner:'accent','Branch Manager':'primary','Sales Rep':'info',Cashier:'success',Accountant:'warning',Supervisor:'primary','Inventory Staff':'warning'};
const logoUrl=v=>!v?null:v.startsWith('http')||v.startsWith('blob')?v:`/api${v}`;

/* ─── Branch Avatar ─────────────────────────────────────────── */
function BranchAvatar({branch,size=52,radius=12}){
  const[err,setErr]=useState(false);
  const src=logoUrl(branch.logo_url);
  const initials=(branch.code||branch.name||'?').substring(0,2).toUpperCase();
  return(
    <div style={{width:size,height:size,borderRadius:radius,overflow:'hidden',flexShrink:0,
      background:branch.is_headquarters?'var(--am)':'var(--pm)',
      display:'flex',alignItems:'center',justifyContent:'center',
      border:'1px solid var(--border)',position:'relative'}}>
      {src&&!err?(
        <img src={src} alt={branch.name} style={{width:'100%',height:'100%',objectFit:'contain',padding:4}} onError={()=>setErr(true)}/>
      ):(
        <span style={{fontSize:size*0.28,fontWeight:800,color:branch.is_headquarters?'var(--accent)':'var(--primary)'}}>{initials}</span>
      )}
    </div>
  );
}

/* ─── Logo Upload ───────────────────────────────────────────── */
function LogoUpload({value,onChange}){
  const{t}=useLang();
  const ref=useRef();
  const[dragging,setDragging]=useState(false);
  const[uploading,setUploading]=useState(false);
  const[urlMode,setUrlMode]=useState(false);
  const[urlVal,setUrlVal]=useState(value||'');
  const[previewErr,setPreviewErr]=useState(false);
  useEffect(()=>{setUrlVal(value||'');setPreviewErr(false)},[value]);
  const doUpload=async file=>{
    if(!file)return;
    if(!file.type.startsWith('image/'))return toast.error(t('common.imageFilesOnly'));
    if(file.size>5*1024*1024)return toast.error(t('common.max5mb'));
    setUploading(true);
    try{const r=await uploadAPI.single(file);onChange(r.data.url);toast.success(t('branches.logoUploaded'))}
    catch{toast.error(t('branches.uploadFailed'))}finally{setUploading(false)};
  };
  const onDrop=e=>{e.preventDefault();setDragging(false);doUpload(e.dataTransfer.files[0])};
  const src=logoUrl(value);
  return(
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.5,textTransform:'uppercase'}}>{t('branches.logoUpload')}</div>
        <button type="button" className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setUrlMode(v=>!v)}>
          {urlMode?<><Upload size={11}/> {t('branches.uploadFile')}</>:<><Link size={11}/> {t('branches.useUrl')}</>}
        </button>
      </div>
      {urlMode?(
        <div>
          <input value={urlVal} onChange={e=>{setUrlVal(e.target.value);setPreviewErr(false)}}
            placeholder="https://example.com/logo.png"
            style={{width:'100%',padding:'8px 12px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13,boxSizing:'border-box'}}/>
          <div style={{display:'flex',gap:8,marginTop:8}}>
            <button type="button" className="btn btn-primary btn-sm" onClick={()=>{onChange(urlVal);toast.success(t('branches.urlApplied'))}}><Check size={12}/> {t('common.apply')}</button>
            {value&&<button type="button" className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={()=>{onChange('');setUrlVal('')}}><X size={12}/> {t('common.remove')}</button>}
          </div>
          {urlVal&&!previewErr&&<img src={urlVal} alt="preview" style={{marginTop:10,height:50,borderRadius:8,border:'1px solid var(--border)',objectFit:'contain',background:'var(--s2)',padding:4}} onError={()=>setPreviewErr(true)}/>}
          {urlVal&&previewErr&&<div style={{fontSize:11,color:'var(--danger)',marginTop:6}}>⚠ {t('branches.cannotLoadImage')}</div>}
        </div>
      ):(
        <div style={{display:'flex',gap:14,alignItems:'flex-start'}}>
          <div onDrop={onDrop} onDragOver={e=>{e.preventDefault();setDragging(true)}} onDragLeave={()=>setDragging(false)}
            onClick={()=>!uploading&&ref.current?.click()}
            style={{width:96,height:96,borderRadius:14,flexShrink:0,cursor:'pointer',overflow:'hidden',
              border:`2px dashed ${dragging?'var(--primary)':'var(--border)'}`,background:dragging?'var(--pm)':'var(--s2)',
              display:'flex',alignItems:'center',justifyContent:'center',transition:'border-color .15s,background .15s',position:'relative'}}>
            {uploading?<RefreshCw size={24} color="var(--primary)" style={{animation:'spin 1s linear infinite'}}/>:
             src&&!previewErr?<img src={src} alt="logo" style={{width:'100%',height:'100%',objectFit:'contain',padding:6}} onError={()=>setPreviewErr(true)}/>:
             <div style={{textAlign:'center'}}><Upload size={24} color="var(--td)"/><div style={{fontSize:10,color:'var(--td)',marginTop:5}}>{t('branches.dropOrClick')}</div></div>}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,color:'var(--td)',lineHeight:1.7,marginBottom:10}}>{t('branches.imageFormats')}</div>
            <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={()=>ref.current?.click()} disabled={uploading}>
                <Image size={12}/> {value?t('branches.changeFile'):t('branches.chooseFile')}
              </button>
              {value&&<button type="button" className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={()=>{onChange('');setPreviewErr(false)}}><X size={12}/> {t('common.remove')}</button>}
            </div>
          </div>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" style={{display:'none'}} onChange={e=>doUpload(e.target.files[0])}/>
    </div>
  );
}

/* ─── Telegram Row ──────────────────────────────────────────── */
function TelegramRow({branch,onSaved}){
  const{t}=useLang();
  const[editing,setEditing]=useState(false);
  const[val,setVal]=useState(branch.telegram_chat_id||'');
  const[saving,setSaving]=useState(false);
  const[testing,setTesting]=useState(false);
  const ok=!!(branch.telegram_chat_id);
  const save=async()=>{
    setSaving(true);
    try{await notificationAPI.updateBranchTelegram(branch.id,{telegram_chat_id:val.trim()||null});toast.success(t('common.saved'));onSaved();setEditing(false);}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}finally{setSaving(false)};
  };
  const test=async()=>{
    setTesting(true);
    try{await notificationAPI.testTelegram({branch_id:branch.id,message:`🔔 Test from ERPhub\n\n📍 ${branch.name} ✅ Connected`});toast.success(t('settings.testSent'));}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}finally{setTesting(false)};
  };
  return(
    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',padding:'7px 10px',borderRadius:8,
      background:ok?'var(--sm)':'var(--s3)',border:`1px solid ${ok?'rgba(34,197,94,.2)':'var(--border)'}`}}>
      {editing?(
        <>
          <MessageSquare size={12} color="var(--primary)"/>
          <input value={val} onChange={e=>setVal(e.target.value)} placeholder="-100123456789 or @channel" autoFocus
            onKeyDown={e=>e.key==='Enter'?save():e.key==='Escape'&&(setEditing(false),setVal(branch.telegram_chat_id||''))}
            style={{padding:'4px 9px',borderRadius:6,border:'1px solid var(--primary)',background:'var(--s3)',color:'var(--text)',fontSize:12,width:185,fontFamily:'monospace'}}/>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} style={{padding:'3px 9px'}}>
            {saving?<RefreshCw size={11} style={{animation:'spin 1s linear infinite'}}/>:<Check size={11}/>}
          </button>
          <button className="btn btn-ghost btn-sm" style={{padding:'3px 8px'}} onClick={()=>{setEditing(false);setVal(branch.telegram_chat_id||'')}}>
            <X size={11}/>
          </button>
        </>
      ):(
        <>
          {ok?<CheckCircle size={12} color="var(--success)"/>:<MessageSquare size={12} color="var(--td)"/>}
          <span style={{fontSize:11,color:ok?'var(--success)':'var(--td)',fontFamily:ok?'monospace':'inherit',fontWeight:ok?600:400}}>
            {ok?branch.telegram_chat_id:t('branches.telegramNotConfigured')}
          </span>
          <button className="btn btn-ghost btn-sm" style={{padding:'2px 7px',fontSize:11,marginLeft:'auto'}} onClick={()=>setEditing(true)}>
            <Edit2 size={10}/> {ok?t('branches.telegramEdit'):t('branches.telegramSet')}
          </button>
          {ok&&<>
            <button className="btn btn-ghost btn-sm" style={{padding:'2px 7px',fontSize:11,color:'var(--info)'}} onClick={test} disabled={testing}>
              {testing?<RefreshCw size={10} style={{animation:'spin 1s linear infinite'}}/>:<Send size={10}/>} {t('branches.telegramTest')}
            </button>
            <button className="btn btn-ghost btn-sm" style={{padding:'2px 6px',color:'var(--danger)'}}
              onClick={async()=>{await notificationAPI.updateBranchTelegram(branch.id,{telegram_chat_id:null});toast.success(t('common.deleted'));onSaved();}}>
              <X size={10}/>
            </button>
          </>}
        </>
      )}
    </div>
  );
}

/* ─── QR Code Modal ─────────────────────────────────────────── */
function QrModal({branch,onClose}){
  const{t}=useLang();
  const menuUrl=`${window.location.origin}/menu/${branch.code}`;
  const svgRef=useRef(null);
  const copyUrl=()=>{navigator.clipboard.writeText(menuUrl);toast.success(t('common.linkCopied'));};
  const downloadSvg=()=>{
    const svg=svgRef.current?.querySelector('svg');if(!svg)return;
    const blob=new Blob([svg.outerHTML],{type:'image/svg+xml'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`menu-qr-${branch.code}.svg`;a.click();URL.revokeObjectURL(a.href);
  };
  const downloadPng=()=>{
    const svg=svgRef.current?.querySelector('svg');if(!svg)return;
    const canvas=document.createElement('canvas');canvas.width=600;canvas.height=600;
    const ctx=canvas.getContext('2d');const img=new window.Image();
    const xml=new XMLSerializer().serializeToString(svg);
    const blob=new Blob([xml],{type:'image/svg+xml'});const url=URL.createObjectURL(blob);
    img.onload=()=>{ctx.fillStyle='#fff';ctx.fillRect(0,0,600,600);ctx.drawImage(img,0,0,600,600);
      const a=document.createElement('a');a.download=`menu-qr-${branch.code}.png`;a.href=canvas.toDataURL('image/png');a.click();URL.revokeObjectURL(url);};
    img.src=url;
  };
  return(
    <Modal open onClose={onClose} title={`e-Menu QR — ${branch.name}`} width={460}>
      <div style={{textAlign:'center'}}>
        <div style={{background:'linear-gradient(135deg,#f8fafc,#eef2ff)',border:'1px solid #e0e7ff',borderRadius:20,padding:'28px 24px 20px',marginBottom:20,display:'inline-block',width:'100%'}}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:'#4f46e5',letterSpacing:.3}}>{branch.name}</div>
            <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>Scan to order · ស្កែនដើម្បីបញ្ជាទិញ</div>
          </div>
          <div ref={svgRef} style={{display:'inline-block',padding:12,background:'#fff',borderRadius:16,boxShadow:'0 4px 20px rgba(99,102,241,.15)',marginBottom:14}}>
            <QRCodeSVG value={menuUrl} size={200} bgColor="#ffffff" fgColor="#1e293b" level="H" includeMargin={false}
              imageSettings={branch.logo_url?{src:branch.logo_url.startsWith('http')?branch.logo_url:`${window.location.origin}${branch.logo_url}`,height:36,width:36,excavate:true}:undefined}/>
          </div>
          <div style={{fontSize:11,color:'#64748b',fontStyle:'italic'}}>{branch.code} · {branch.city||''}</div>
        </div>
        <div style={{display:'flex',gap:6,marginBottom:16,alignItems:'center'}}>
          <div style={{flex:1,padding:'9px 12px',background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)',fontSize:11,color:'var(--td)',fontFamily:'monospace',textAlign:'left',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{menuUrl}</div>
          <button onClick={copyUrl} style={{padding:'9px 12px',background:'var(--pm)',border:'1px solid var(--primary)',borderRadius:10,cursor:'pointer',color:'var(--primary)',display:'flex',alignItems:'center',gap:5,fontSize:12,fontWeight:700,flexShrink:0}}>
            <Copy size={13}/>{t('common.share')}
          </button>
          <a href={menuUrl} target="_blank" rel="noreferrer" style={{padding:'9px 10px',background:'var(--pm)',border:'1px solid var(--primary)',borderRadius:10,color:'var(--primary)',display:'flex',alignItems:'center',textDecoration:'none',flexShrink:0}}>
            <ExternalLink size={14}/>
          </a>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:14}}>
          <button onClick={downloadSvg} style={{padding:'10px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,cursor:'pointer',color:'var(--text)',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <Download size={13}/>Download SVG
          </button>
          <button onClick={downloadPng} style={{padding:'10px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,cursor:'pointer',color:'var(--text)',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <Download size={13}/>Download PNG
          </button>
        </div>
        <div style={{background:'var(--s2)',borderRadius:12,padding:'12px 14px',textAlign:'left',fontSize:12,color:'var(--td)',lineHeight:1.7}}>
          <div style={{fontWeight:700,color:'var(--tm)',marginBottom:4,display:'flex',alignItems:'center',gap:5}}><Smartphone size={13}/>How to use</div>
          <div>• Print &amp; place at your table or counter</div>
          <div>• Customers scan to browse and order</div>
          <div>• Orders appear in your <b>Orders</b> page instantly</div>
          <div>• Enable Telegram to receive live order alerts</div>
        </div>
      </div>
      <div className="modal-footer" style={{marginTop:16}}>
        <button className="btn btn-ghost" onClick={onClose}>{t('common.close')}</button>
      </div>
    </Modal>
  );
}

/* ─── Branch Card ───────────────────────────────────────────── */
function BranchCard({branch,can,isOwner,onEdit,onToggle,onDelete,onAssign,onQr,onReload,allUsers,t}){
  const[expanded,setExpanded]=useState(false);
  const users=branch.users||[];
  const removeFromBranch=async u=>{
    try{await userAPI.update(u.id,{branch_id:null});toast.success(`${u.first_name} ${t('branches.removeFromBranch')}`);onReload();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))};
  };
  return(
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden',transition:'box-shadow .15s'}}>
      {/* Header */}
      <div style={{padding:'16px 18px'}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:14,flexWrap:'wrap'}}>
          <BranchAvatar branch={branch} size={56} radius={13}/>
          <div style={{flex:1,minWidth:0}}>
            {/* Name row */}
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5,flexWrap:'wrap'}}>
              <span style={{fontSize:16,fontWeight:800}}>{branch.name}</span>
              <span style={{fontSize:11,fontWeight:700,color:'var(--primary)',background:'var(--pm)',padding:'2px 8px',borderRadius:5}}>{branch.code}</span>
              {branch.is_headquarters&&<span style={{fontSize:11,fontWeight:700,color:'var(--accent)',background:'var(--am)',padding:'2px 8px',borderRadius:5}}>HQ</span>}
              <Badge variant={branch.is_active?'success':'danger'}>{branch.is_active?t('common.active'):t('common.inactive')}</Badge>
            </div>
            {/* Contact pills */}
            <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:10}}>
              {branch.city&&<span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}><MapPin size={11}/>{branch.city}</span>}
              {branch.phone&&<span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}><Phone size={11}/>{branch.phone}</span>}
              {branch.email&&<span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}><Mail size={11}/>{branch.email}</span>}
              {(branch.opening_time||branch.closing_time)&&<span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}><Clock size={11}/>{branch.opening_time}–{branch.closing_time}</span>}
              {branch.manager_name&&<span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}><Shield size={11}/>{branch.manager_name}</span>}
              {branch.tax_rate>0&&<span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}>Tax {branch.tax_rate}%</span>}
              <span style={{fontSize:12,color:'var(--tm)',display:'flex',alignItems:'center',gap:4}}><Users size={11}/>{users.length} {t('nav.users').toLowerCase()}</span>
              <a href={`/menu/${branch.code}`} target="_blank" rel="noreferrer"
                style={{fontSize:11,color:'var(--primary)',display:'flex',alignItems:'center',gap:3,fontWeight:700,background:'var(--pm)',padding:'2px 8px',borderRadius:6,textDecoration:'none'}}>
                <QrCode size={10}/>{t('branches.eMmenu')}
              </a>
            </div>
            {/* Telegram row */}
            {can.edit
              ?<TelegramRow branch={branch} onSaved={onReload}/>
              :<div style={{display:'flex',alignItems:'center',gap:6,fontSize:11}}>
                {branch.telegram_chat_id
                  ?<><CheckCircle size={11} color="var(--success)"/><span style={{color:'var(--success)',fontFamily:'monospace'}}>{branch.telegram_chat_id}</span></>
                  :<><XCircle size={11} color="var(--td)"/><span style={{color:'var(--td)'}}>{t('branches.telegramNotConfigured')}</span></>}
              </div>}
          </div>
          {/* Action buttons */}
          <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center',flexWrap:'wrap'}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>onQr(branch)} title="e-Menu QR" style={{color:'var(--primary)'}}><QrCode size={15}/></button>
            {can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>onToggle(branch)} title={branch.is_active?t('common.deactivate'):t('common.activate')}>
              {branch.is_active?<ToggleRight size={16} color="var(--success)"/>:<ToggleLeft size={16}/>}
            </button>}
            {can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>onEdit(branch)}><Edit size={14}/></button>}
            {can.delete&&isOwner&&<button className="btn btn-danger btn-sm" onClick={()=>onDelete(branch.id)}><Trash2 size={14}/></button>}
            <button className="btn btn-ghost btn-sm" style={{display:'flex',alignItems:'center',gap:4,fontSize:12}}
              onClick={()=>setExpanded(v=>!v)}>
              <Users size={14}/>{expanded?<ChevronUp size={13}/>:<ChevronDown size={13}/>}
            </button>
          </div>
        </div>
      </div>
      {/* Users Panel */}
      {expanded&&(
        <div style={{borderTop:'1px solid var(--border)',background:'var(--s2)',padding:'14px 18px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <span style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.5}}>
              {t('branches.assignedUsers',{n:users.length})}
            </span>
            {can.manageUsers&&<button className="btn btn-primary btn-sm" onClick={()=>onAssign(branch)}><Plus size={13}/> {t('branches.assignUser')}</button>}
          </div>
          {users.length===0
            ?<div style={{textAlign:'center',padding:'18px 0',color:'var(--td)',fontSize:13}}>{t('branches.noUsersAssigned')}</div>
            :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:9}}>
              {users.map(u=>(
                <div key={u.id} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'10px 13px',display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:35,height:35,borderRadius:9,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'var(--primary)',flexShrink:0}}>
                    {(u.first_name?.[0]||'')+(u.last_name?.[0]||'')}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.first_name} {u.last_name}</div>
                    <div style={{fontSize:11,color:'var(--td)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.email}</div>
                    <div style={{marginTop:3,display:'flex',gap:5,flexWrap:'wrap'}}>
                      <span style={{fontSize:10,fontWeight:700,color:`var(--${ROLE_COLOR[u.role?.name]||'primary'})`,background:`var(--${ROLE_COLOR[u.role?.name]||'primary'}m)`,padding:'1px 6px',borderRadius:4}}>{u.role?.name||'No role'}</span>
                      {!u.is_active&&<span style={{fontSize:10,fontWeight:700,color:'var(--danger)',background:'var(--dm)',padding:'1px 6px',borderRadius:4}}>{t('common.inactive')}</span>}
                    </div>
                  </div>
                  {can.manageUsers&&<button className="btn btn-ghost btn-sm" title={t('branches.removeFromBranch')}
                    style={{color:'var(--danger)',flexShrink:0,padding:'4px 6px'}} onClick={()=>removeFromBranch(u)}><Trash2 size={13}/></button>}
                </div>
              ))}
            </div>}
        </div>
      )}
    </div>
  );
}

/* ═══ MAIN PAGE ════════════════════════════════════════════════ */
export default function BranchesPage(){
  const{t}=useLang();
  const{hasPermission,isOwner,myBranchId,user}=useAuth();
  const can={
    create:isOwner||hasPermission('branches.create'),
    edit:isOwner||hasPermission('branches.edit'),
    delete:isOwner||hasPermission('branches.delete'),
    manageUsers:isOwner||hasPermission('users.edit'),
  };

  const[data,setData]=useState([]);
  const[allUsers,setAllUsers]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  const[modal,setModal]=useState(null);
  const[form,setForm]=useState({});
  const[delId,setDelId]=useState(null);
  const[assignModal,setAssignModal]=useState(null);
  const[assignUserId,setAssignUserId]=useState('');
  const[qrBranch,setQrBranch]=useState(null);

  const load=useCallback(async()=>{
    try{
      let branches=[];
      if(isOwner){
        // Owner sees all branches
        const r=await branchAPI.getAll({search,limit:100});
        branches=r.data.data||[];
      } else if(myBranchId){
        // Non-owner: only their own branch
        const r=await branchAPI.getAll({limit:100});
        const all=r.data.data||[];
        branches=all.filter(b=>b.id===myBranchId);
      }
      setData(branches);
    }catch{toast.error(t('branches.failedLoad'))}
    finally{setLd(false)};
  },[search,isOwner,myBranchId]);

  useEffect(()=>{const id=setTimeout(load,300);return()=>clearTimeout(id)},[load]);
  useEffect(()=>{if(isOwner)userAPI.getAll({limit:500}).then(r=>setAllUsers(r.data.data||[])).catch(()=>{})},[isOwner]);

  const blank={name:'',code:'',address:'',city:'',phone:'',email:'',manager_name:'',
    tax_rate:0,opening_time:'08:00',closing_time:'20:00',is_active:true,
    is_headquarters:false,telegram_chat_id:'',logo_url:''};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{
    if(!form.name||!form.code)return toast.error(t('branches.nameCodeRequired'));
    try{
      const payload={...form,tax_rate:parseFloat(form.tax_rate)||0};
      if(modal==='new'){await branchAPI.create(payload);toast.success(t('branches.created'));}
      else{await branchAPI.update(form.id,payload);toast.success(t('branches.updated'));}
      setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))};
  };

  const toggle=async b=>{
    try{await branchAPI.update(b.id,{is_active:!b.is_active});toast.success(b.is_active?t('common.deactivated'):t('common.activated'));load();}
    catch{toast.error(t('common.failedToSave'))};
  };

  const del=async()=>{
    try{await branchAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))};
  };

  const doAssign=async()=>{
    if(!assignUserId)return toast.error(t('branches.selectUser'));
    try{await userAPI.update(parseInt(assignUserId),{branch_id:assignModal.id});toast.success(t('users.assigned'));setAssignModal(null);setAssignUserId('');load();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))};
  };

  const unassignedUsers=assignModal?allUsers.filter(u=>u.branch_id!==assignModal.id):[];
  const userOpts=[{value:'',label:t('branches.selectUser')},...unassignedUsers.map(u=>({
    value:u.id,label:`${u.first_name} ${u.last_name} (${u.role?.name||'?'})${u.branch?.name?' — '+u.branch.name:''}`
  }))];

  const configuredCount=data.filter(b=>b.telegram_chat_id).length;
  const logoCount=data.filter(b=>b.logo_url).length;

  // Subtitle: show different text for owners vs branch users
  const subtitle=isOwner
    ? t('branches.subtitle',{total:data.length,telegram:configuredCount,logo:logoCount})
    : data.length>0
      ? t('branches.viewingBranch',{name:data[0]?.name||''})
      : t('branches.noBranchAssigned');

  return(
    <div>
      <PageHeader
        title={t('branches.title')}
        subtitle={subtitle}
        action={can.create&&isOwner&&(
          <button className="btn btn-primary" onClick={()=>{setForm(blank);setModal('new')}}>
            <Plus size={16}/> {t('branches.newBranch')}
          </button>
        )}
      />

      {/* Non-owner notice banner */}
      {!isOwner&&(
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',
          background:'rgba(0,212,170,0.06)',border:'1px solid rgba(0,212,170,0.2)',
          borderRadius:10,marginBottom:16,fontSize:13}}>
          <Lock size={14} color="var(--primary)"/>
          <span style={{color:'var(--primary)',fontWeight:600}}>{t('branches.yourBranchOnly')}</span>
          {data.length>0&&<span style={{color:'var(--td)',marginLeft:4}}>— <strong style={{color:'var(--text)'}}>{data[0]?.name}</strong></span>}
        </div>
      )}

      {/* Search (owner only) */}
      {isOwner&&<SearchBar value={search} onChange={setSearch} placeholder={t('branches.searchPlaceholder')}/>}

      {/* Branch list */}
      {ld?(
        <div style={{textAlign:'center',padding:40,color:'var(--td)'}}>{t('common.loading')}</div>
      ):data.length===0?(
        <div style={{textAlign:'center',padding:40,color:'var(--td)'}}>
          <Building2 size={40} color="var(--border)" style={{margin:'0 auto 12px'}}/>
          <div>{isOwner?t('branches.noBranches'):t('branches.noBranchAssigned')}</div>
        </div>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {data.map(branch=>(
            <BranchCard
              key={branch.id}
              branch={branch}
              can={can}
              isOwner={isOwner}
              allUsers={allUsers}
              t={t}
              onEdit={b=>{setForm({...b,logo_url:b.logo_url||'',telegram_chat_id:b.telegram_chat_id||''});setModal('edit');}}
              onToggle={toggle}
              onDelete={id=>setDelId(id)}
              onAssign={b=>{setAssignModal(b);setAssignUserId('');}}
              onQr={b=>setQrBranch(b)}
              onReload={load}
            />
          ))}
        </div>
      )}

      {/* ═══ Create / Edit Modal ═══ */}
      <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)}
        title={modal==='new'?t('branches.newBranch'):t('branches.editBranch')} width={720}>
        {/* Logo preview */}
        <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:16,marginBottom:18,padding:16,background:'var(--s2)',borderRadius:12,border:'1px solid var(--border)'}}>
          <LogoUpload value={form.logo_url||''} onChange={v=>F('logo_url',v)}/>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:6,padding:'12px 16px',borderRadius:10,background:'var(--s3)',minWidth:110}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.5,marginBottom:4}}>{t('branches.preview')}</div>
            <BranchAvatar branch={{...form,logo_url:form.logo_url}} size={64} radius={14}/>
            <div style={{fontSize:11,fontWeight:700,textAlign:'center',maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{form.name||t('branches.branchName').replace(' *','')}</div>
            <div style={{fontSize:10,color:'var(--td)'}}>{form.code||t('branches.branchCode').replace(' *','')}</div>
          </div>
        </div>
        {/* Fields */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
          <FormInput label={t('branches.branchName')} value={form.name||''} onChange={v=>F('name',v)} required placeholder={t('branches.branchName').replace(' *','')}/>
          <FormInput label={t('branches.branchCode')} value={form.code||''} onChange={v=>F('code',v)} required placeholder="e.g. PP-HQ"/>
          <FormInput label={t('branches.city')} value={form.city||''} onChange={v=>F('city',v)} placeholder="Phnom Penh"/>
          <FormInput label={t('branches.phone')} value={form.phone||''} onChange={v=>F('phone',v)} placeholder="+855 xx xxx xxx"/>
          <FormInput label={t('branches.email')} value={form.email||''} onChange={v=>F('email',v)} placeholder="branch@company.com"/>
          <FormInput label={t('branches.managerName')} value={form.manager_name||''} onChange={v=>F('manager_name',v)} placeholder="Full name"/>
          <FormInput label={t('branches.taxRate')} value={String(form.tax_rate||0)} onChange={v=>F('tax_rate',v)} type="number"/>
          <div/>
          <FormInput label={t('branches.openingTime')} value={form.opening_time||'08:00'} onChange={v=>F('opening_time',v)} placeholder="08:00"/>
          <FormInput label={t('branches.closingTime')} value={form.closing_time||'20:00'} onChange={v=>F('closing_time',v)} placeholder="20:00"/>
          <FormInput label={t('branches.isHeadquarters')} value={form.is_headquarters?'true':'false'} onChange={v=>F('is_headquarters',v==='true')}
            options={[{value:'false',label:t('branches.noRegular')},{value:'true',label:t('branches.yesHq')}]}/>
          <FormInput label={t('branches.isActive')} value={form.is_active?'true':'false'} onChange={v=>F('is_active',v==='true')}
            options={[{value:'true',label:t('common.active')},{value:'false',label:t('common.inactive')}]}/>
        </div>
        <FormInput label={t('branches.address')} value={form.address||''} onChange={v=>F('address',v)} placeholder={t('branches.fullAddress')}/>
        {/* Telegram */}
        <div style={{marginTop:6,padding:'14px 16px',background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <MessageSquare size={14} color="var(--primary)"/>
            <span style={{fontSize:13,fontWeight:700}}>{t('branches.telegramNotifications')}</span>
            {form.telegram_chat_id
              ?<span style={{fontSize:11,color:'var(--success)',display:'flex',alignItems:'center',gap:4,marginLeft:4}}><CheckCircle size={11}/> {t('branches.telegramConfigured')}</span>
              :<span style={{fontSize:11,color:'var(--td)',marginLeft:4}}>{t('branches.telegramNotConfigured')}</span>}
          </div>
          <FormInput label={t('branches.telegramChatId')} value={form.telegram_chat_id||''} onChange={v=>F('telegram_chat_id',v)} placeholder="-100123456789  or  @channelname"/>
          <div style={{fontSize:11,color:'var(--td)',marginTop:-4,lineHeight:1.7}}>
            💡 {t('branches.telegramHint')}&nbsp;
            <code style={{background:'var(--s3)',padding:'0 4px',borderRadius:3}}>@userinfobot</code>&nbsp;·&nbsp;
            <code style={{background:'var(--s3)',padding:'0 4px',borderRadius:3}}>-100...</code>
          </div>
        </div>
        <div className="modal-footer" style={{marginTop:18}}>
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={save}>
            {modal==='new'?t('branches.createBranch'):t('common.save')}
          </button>
        </div>
      </Modal>

      {/* ═══ Assign User Modal ═══ */}
      <Modal open={!!assignModal} onClose={()=>setAssignModal(null)}
        title={`${t('branches.assignUser')} — ${assignModal?.name}`} width={480}>
        {assignModal&&<>
          <p style={{fontSize:13,color:'var(--tm)',marginBottom:16}}>
            Select a user to assign to this branch. Users belong to one branch at a time.
          </p>
          <FormInput label={t('common.user')} value={String(assignUserId)} onChange={v=>setAssignUserId(v)} options={userOpts}/>
          {assignUserId&&(()=>{
            const u=allUsers.find(x=>x.id===parseInt(assignUserId));
            return u?.branch_id&&u.branch_id!==assignModal.id?(
              <div style={{padding:'8px 12px',background:'var(--wm)',borderRadius:8,fontSize:12,color:'var(--warning)',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                <AlertTriangle size={13}/> {t('branches.userWillMove',{branch:u.branch?.name||'another branch'})}
              </div>
            ):null;
          })()}
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={()=>setAssignModal(null)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={doAssign} disabled={!assignUserId}>
              <Users size={14}/> {t('branches.assignUser')}
            </button>
          </div>
        </>}
      </Modal>

      {/* ═══ Delete Confirm ═══ */}
      <Modal open={!!delId} onClose={()=>setDelId(null)} title={t('branches.deleteBranch')} width={400}>
        <div style={{textAlign:'center',padding:'10px 0 18px'}}>
          <div style={{width:50,height:50,borderRadius:12,background:'var(--dm)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
            <AlertTriangle size={22} color="var(--danger)"/>
          </div>
          <p style={{fontSize:14,color:'var(--tm)'}}>{t('branches.deleteWarning')}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setDelId(null)}>{t('common.cancel')}</button>
          <button className="btn btn-danger" onClick={del}><Trash2 size={14}/> {t('branches.deleteBranch')}</button>
        </div>
      </Modal>

      {/* ═══ QR Code Modal ═══ */}
      {qrBranch&&<QrModal branch={qrBranch} onClose={()=>setQrBranch(null)}/>}
    </div>
  );
}
